import type { DonutSegment } from '../types/analytics.types'

// The mockup uses r=15.9155 in a 40x40 viewBox.
// Circumference = 2 * π * r ≈ 100.0 — intentional so percentages map 1:1 to stroke-dasharray.
const CIRCUMFERENCE = 100

interface ComputedDonutSegment {
  strokeDasharray: string
  strokeDashoffset: number
  color: string
  label: string
  percentage: number
}

/**
 * Converts an array of DonutSegment (percentage + color + label) into
 * SVG stroke-dasharray / stroke-dashoffset values for stacked circle segments.
 *
 * Percentages are normalised to sum to 100 before computing offsets.
 */
export function buildDonutSegments(segments: DonutSegment[]): ComputedDonutSegment[] {
  if (!segments.length) return []

  const total = segments.reduce((sum, s) => sum + s.percentage, 0)
  const normalise = total > 0 ? CIRCUMFERENCE / total : 0

  let cumulative = 0
  return segments.map((seg) => {
    const dash = seg.percentage * normalise
    const offset = cumulative === 0 ? 0 : -cumulative
    cumulative += dash
    return {
      strokeDasharray: `${dash} ${CIRCUMFERENCE}`,
      strokeDashoffset: offset,
      color: seg.color,
      label: seg.label,
      percentage: seg.percentage
    }
  })
}

/**
 * Builds an SVG path string for a smooth line chart using quadratic bezier curves.
 * Maps data values onto [0, svgHeight] (inverted: high value = low y).
 * Maps index onto [0, svgWidth].
 */
export function buildLinePath(
  points: number[],
  svgWidth: number,
  svgHeight: number,
  valueMin: number,
  valueMax: number
): string {
  if (!points.length) return ''
  if (points.length === 1) {
    const y = svgHeight / 2
    return `M0,${y} L${svgWidth},${y}`
  }

  const range = valueMax - valueMin || 1
  const xStep = svgWidth / (points.length - 1)

  const toX = (i: number) => i * xStep
  const toY = (v: number) => svgHeight - ((v - valueMin) / range) * svgHeight

  let path = `M${toX(0)},${toY(points[0])}`

  for (let i = 1; i < points.length; i++) {
    const cpX = toX(i - 0.5)
    const cpY = toY(points[i - 1])
    path += ` Q${cpX},${cpY} ${toX(i)},${toY(points[i])}`
  }

  return path
}

/**
 * Produces 5 evenly-spaced y-axis tick labels from 0 to max.
 * Returned from top to bottom (highest first) to match SVG coordinate direction.
 */
export function buildYAxisTicks(max: number): string[] {
  const step = max / 4
  return [4, 3, 2, 1, 0].map((i) => {
    const value = i * step
    if (value >= 1000) return `${Math.round(value / 1000)}k`
    return String(Math.round(value))
  })
}
